import React, { useRef, useState } from "react";
import { Toast } from "primereact/toast";
import { apiService } from "../../../api/api";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import CSVReader from "react-csv-reader";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tooltip } from "primereact/tooltip";
import { trackToolAction } from "../../../utils/analytics";

interface UploadToolRow {
  title: string;
  author: string;
  summary: string;
  seasonNumber: number;
  episodeNumber: number;
  status: string;
  draft: string;
  backgroundAudioFileUrl: string;
  podcast: string;
}

const UploadTool: React.FC = () => {
  const [uploadToolRow, setUploadToolRow] = useState<UploadToolRow[]>([]);
  const [invalidRows, setInvalidRows] = useState<UploadToolRow[]>([]);
  const [response, setResponse] = useState<Array<{
    success: boolean;
    data?: any;
    error?: string | undefined;
    status?: number | undefined;
  }> | null>(null);
  const toast = useRef<Toast>(null);

  // Validate row format
  const isValidRow = (row: UploadToolRow): boolean => {
    return (
      typeof row.title === "string" &&
      typeof row.author === "string" &&
      typeof row.summary === "string" &&
      typeof row.seasonNumber === "number" &&
      !isNaN(row.seasonNumber) && // Check for number AND not NaN
      typeof row.episodeNumber === "number" &&
      !isNaN(row.episodeNumber) && // Check for number AND not NaN
      typeof row.status === "string" &&
      typeof row.draft === "string" &&
      typeof row.backgroundAudioFileUrl === "string" &&
      (row.backgroundAudioFileUrl.endsWith(".mp3") ||
        row.backgroundAudioFileUrl.endsWith(".mp4")) &&
      typeof row.podcast === "string"
    );
  };

  const shortenURL = (url: string, maxLength: number = 40) => {
    if (url.length <= maxLength) {
      return url;
    }
    const tailLength = Math.floor(maxLength / 2); // Adjust as needed
    const tail = url.substring(url.length - tailLength);
    return `...${tail}`;
  };

  // Process CSV data
  const handleCSVUpload = (data: string[][]) => {
    trackToolAction("spotifyUploader", "csv_upload");
    const validRows: UploadToolRow[] = [];
    const invalidRows: UploadToolRow[] = [];

    data.forEach((row) => {
      const newRow: UploadToolRow = {
        title: row[0]?.trim() || "",
        author: row[1]?.trim() || "",
        summary: row[2]?.trim() || "",
        seasonNumber: parseInt(row[3]?.trim() || ""),
        episodeNumber: parseInt(row[4]?.trim() || ""),
        status: row[5]?.trim() || "",
        draft: row[6]?.trim() || "",
        backgroundAudioFileUrl: row[7]?.trim() || "",
        podcast: row[8]?.trim() || "",
      };

      isValidRow(newRow) ? validRows.push(newRow) : invalidRows.push(newRow);
    });

    setUploadToolRow(validRows);
    setInvalidRows(invalidRows);
  };

  // Handle asset linking
  const handleLinkAssets = async () => {
    if (uploadToolRow.length === 0) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "No valid data to link",
        life: 3000,
      });
      return;
    }

    trackToolAction("spotifyUploader", "run_upload");
    try {
      const response =
        await apiService.spotifyPodcastUploader.uploadSpotifyPodcast(
          uploadToolRow
        );

      // Check for overall success/failure (this assumes your API returns an object with a success property at the top level)
      if (response && Array.isArray(response)) {
        if (response.some((r) => !r.success)) {
          // if any of the upload failed display an error message
          const failedUploads = response
            .filter((r) => !r.success)
            .map((r) => r.error)
            .join(", ");
          toast.current?.show({
            severity: "error",
            summary: "Upload Error",
            detail: failedUploads,
            life: 3000,
          });
        } else {
          toast.current?.show({
            severity: "success",
            summary: "Success",
            detail: "Episodes uploaded successfully!",
            life: 3000,
          });
        }
      }

      setResponse(response);
      setUploadToolRow([]);
    } catch (error: any) {
      let errorMessage = "Error uploading episodes.";
      if (error.response && error.response.data && error.response.data.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.current?.show({
        severity: "error",
        summary: "Upload Failed",
        detail: errorMessage,
        life: 3000,
      });
      console.error("Client-side upload error:", error);
      setResponse([{ success: false, error: errorMessage }]);
    }
  };

  return (
    <Card className="p-4 w-3/4 mx-auto text-center">
      <h2 className="mb-3">Upload a CSV file for podcast episodes</h2>

      <CSVReader
        cssClass="csv-reader-input"
        onFileLoaded={handleCSVUpload}
        onError={() => alert("Oops! Something went wrong.")}
        parserOptions={{ header: false }}
      />

      {response && ( // Check if response exists
        <div className="mt-3">
          <h3>Upload Results</h3>
          <div>
            {response.map((result, index) => (
              <li key={index}>
                {result.success ? (
                  <span className="text-green-600">
                    Successfully uploaded episode: "
                    {result.data?.title || "Episode"}"
                  </span>
                ) : (
                  <span className="text-red-600">
                    Error uploading {result.data?.title || "episode"}:{" "}
                    {typeof result.error === "string"
                      ? result.error
                      : JSON.stringify(result.error)}
                  </span>
                )}
              </li>
            ))}
          </div>
        </div>
      )}
      <div className="mt-4">
        <h3>Valid Data</h3>
        <DataTable value={uploadToolRow} responsiveLayout="scroll">
          <Column field="title" header="Title" />
          <Column field="author" header="Author" />
          <Column field="summary" header="Summary" />
          <Column field="seasonNumber" header="Season Number" />
          <Column field="episodeNumber" header="Episode Number" />
          <Column field="status" header="Ready Status" />
          <Column field="draft" header="Draft Status" />
          <Column
            field="backgroundAudioFileUrl"
            header="Audio File URL"
            body={(rowData) => (
              <div className="flex justify-center">
                <Tooltip target=".truncated-cell" />
                <span
                  className="truncated-cell"
                  data-pr-tooltip={rowData.backgroundAudioFileUrl}
                  style={{
                    maxWidth: "200px", // Adjust as needed
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {shortenURL(rowData.backgroundAudioFileUrl)}{" "}
                  {/* Use the shortenURL function */}
                </span>
              </div>
            )}
          />

          <Column field="podcast" header="Podcast ID" />
        </DataTable>
      </div>
      <div className="mt-4">
        <h3>Invalid Data</h3>
        <DataTable value={invalidRows} responsiveLayout="scroll">
          <Column field="title" header="Title" />
          <Column field="author" header="Author" />
          <Column field="summary" header="Summary" />
          <Column field="seasonNumber" header="Season Number" />
          <Column field="episodeNumber" header="Episode Number" />
          <Column field="status" header="Ready Status" />
          <Column field="draft" header="Draft Status" />
          <Column
            field="backgroundAudioFileUrl"
            header="Audio File URL"
            body={(rowData) => (
              <div className="flex justify-center">
                <Tooltip target=".truncated-cell" />
                <span
                  className="truncated-cell"
                  data-pr-tooltip={rowData.backgroundAudioFileUrl}
                  style={{
                    maxWidth: "200px", // Adjust as needed
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {shortenURL(rowData.backgroundAudioFileUrl)}{" "}
                  {/* Use the shortenURL function */}
                </span>
              </div>
            )}
          />
          <Column field="podcast" header="Podcast ID" />
        </DataTable>
      </div>
      <Button
        label="Run Upload"
        className="p-button-success mt-4"
        onClick={handleLinkAssets}
      />
      <Toast ref={toast} />
    </Card>
  );
};

export default UploadTool;
